import Decimal from "decimal.js";
import type { Nurse, NurseInShift, OpenShiftRequest } from "../../../utils/models";
import { isActivePostedRequest } from "../../../utils/openShiftMatching";
import { filterNursesForStats } from "../../../utils/statsCalculations";
import { safeSum } from "../../../utils/utilFunctions";

/**
 * The metrics drawer's "Pick-up bonuses" row (P3-11, settling P2-3): what the day's open posts are offering, and
 * what the day's staffed shifts have already been promised. Building-wide — the saved scope narrows neither, as it
 * narrows neither Total cost nor PPD beside it. Money: the row is wage-gated by its caller, like Total cost.
 */
export type GapsPickupBonuses = { offered: number; committed: number };

/**
 * What a posted request pays whoever picks it up right now — the read every approve and display path makes
 * (`RequestUtilsProvider.approveAddRequest`, `RequestItem`, `OpenShiftCard`).
 *
 * DEFAULT: `current_bonus` is filled only when the request carries an incentive plan (the backend resolves the
 * decay curve at read time); without one it is absent and `bonus` IS the value — the backend documents this as
 * "clients read current_bonus ?? bonus" (`models/requests.py`). A request offering nothing has `bonus: 0` in the
 * model (backend default `0.0`), so it contributes 0 because it offers 0, not because a value was missing.
 */
export const currentRequestBonus = (request: Pick<OpenShiftRequest, "bonus" | "current_bonus">): number => request.current_bonus ?? request.bonus;

/**
 * Offered = Σ over the day's OPEN posts of the current bonus × the openings still left.
 *
 * `needed_nurses` is the REMAINING openings — every approval decrements it — so a two-person post with one filled
 * offers its bonus once more, not twice. "Open" is `isActivePostedRequest`, the very test the desktop's posted-request
 * matcher applies: the head nurse's feed returns Active requests only, so this drops nothing today, but a request
 * whose status has moved on locally must not keep offering money. Decimal math because these are dollars: summed as
 * floats, a $0.10 bonus on three openings reads $0.30000000000000004.
 */
export const offeredPickupBonus = (dayPostedRequests: readonly OpenShiftRequest[]): number =>
	dayPostedRequests
		.filter(isActivePostedRequest)
		.reduce((total, request) => safeSum(total, new Decimal(currentRequestBonus(request)).mul(request.needed_nurses).toNumber()), 0);

/**
 * The Total cost figure's own inclusion flags. Total cost is `DayStats.totalSpent`, the backend's `regular_wages`
 * (`get_day_wage_stats`): wage rows — a shift's bonus is written as one of them — of shifts that are not Trainee /
 * Orientation / Education (those land in the trainee and orientation buckets) and whose staff count toward PPD.
 * `filterNursesForStats` with both flags off is the frontend's statement of that same set — `calculateDayStats`
 * asks it for the day's hours with exactly these — and it also drops a shift queued for deletion, as the draft-mode
 * stats that replace Total cost while a draft is open do.
 */
const INCLUDE_TRAINEES_IN_TOTAL_COST = false;
const INCLUDE_PPD_EXCLUDED_IN_TOTAL_COST = false;

/**
 * Committed = Σ `NurseInShift.bonus` over the day's staffed shifts that Total cost counts.
 *
 * An approved pickup's bonus is copied onto the shift it creates (a partial pickup's is 0), and the backend adds it
 * into that shift's wage total — so the row can say, truthfully, that this money is already inside Total cost. That
 * only holds if the same shifts are summed, which is why the Total cost set is mirrored rather than every shift.
 */
export const committedPickupBonus = (dayShifts: readonly NurseInShift[], jobManagementEnabled: boolean, nurses: Nurse[]): number =>
	dayShifts
		.filter((shift) => filterNursesForStats(shift, jobManagementEnabled, nurses, INCLUDE_TRAINEES_IN_TOTAL_COST, INCLUDE_PPD_EXCLUDED_IN_TOTAL_COST))
		.reduce((total, shift) => safeSum(total, shift.bonus), 0);

export const pickupBonusesForDay = ({
	dayPostedRequests,
	dayShifts,
	jobManagementEnabled,
	nurses,
}: {
	dayPostedRequests: readonly OpenShiftRequest[];
	dayShifts: readonly NurseInShift[];
	jobManagementEnabled: boolean;
	nurses: Nurse[];
}): GapsPickupBonuses => ({
	offered: offeredPickupBonus(dayPostedRequests),
	committed: committedPickupBonus(dayShifts, jobManagementEnabled, nurses),
});
